import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const isNative = () => Capacitor.isNativePlatform();

/**
 * Open a Stripe (or other) checkout URL.
 *
 * Web: full-page redirect, exactly as before — Stripe's success/cancel URL
 * brings the user back to the site.
 *
 * Native: a full-page redirect would navigate the WebView away from the app
 * (and the https success URL is a different origin from `capacitor://localhost`,
 * so the returning page would be logged-out). Instead we open the URL in an
 * in-app browser sheet *over* the still-mounted app. Our Stripe webhook is the
 * source of truth for the payment outcome, so when the user dismisses the sheet
 * (`browserFinished`) we simply run `onReturn` to let the caller re-sync state
 * (refetch the doc, navigate forward, etc.). No deep links required.
 *
 * @param {string} url - the checkout URL returned by the Cloud Function
 * @param {{ onReturn?: () => (void | Promise<void>) }} [opts]
 */
export async function openCheckout(url, { onReturn } = {}) {
  if (!isNative()) {
    window.location.href = url;
    return;
  }

  // Fires once when the sheet is dismissed (Done button or swipe-down).
  const handle = await Browser.addListener('browserFinished', async () => {
    handle.remove();
    if (typeof onReturn === 'function') {
      try {
        await onReturn();
      } catch (err) {
        console.warn('openCheckout onReturn handler failed:', err);
      }
    }
  });

  await Browser.open({ url });
}

/**
 * Open an external URL such as a Shippo label PDF or a hosted page.
 *
 * Web: new tab (unchanged). Native: in-app browser sheet, where the user can
 * view the PDF and use the iOS share sheet to print/save.
 *
 * @param {string} url
 */
export async function openExternalUrl(url) {
  if (!isNative()) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  await Browser.open({ url });
}
